import { trackDesignSystemAction } from 'analytics';
import forge from 'apis/forge';
import { DesignSystem } from 'dragonmouse-shared/interfaces';
import { useCallback, useMemo, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectUser } from 'redux/db/reducers';
import { designSystemEditingBlocked } from 'redux/db/util';
import { selectIsMobile } from 'redux/mode/reducers';
import { RootReducer } from 'redux/reducers';
import { designSystemShareUrl } from 'utils';
import ShareEntityModal, { ShareEntityModalConfig } from '../ShareEntityModal';

interface ShareDesignSystemModalProps {
	designSystem: DesignSystem;
	onClose: () => void;
}

const ShareDesignSystemModal = ({ designSystem, onClose }: ShareDesignSystemModalProps) => {
	const [publiclyReadable, setPubliclyReadable] = useState(
		!!designSystem?.metadata?.publiclyReadable
	);

	const { isMobile, user } = useSelector((r: RootReducer) => {
		return {
			isMobile: selectIsMobile(r.mode),
			user: selectUser(r.db),
		};
	}, shallowEqual);
	const canNativeShare =
		isMobile && typeof navigator !== 'undefined' && typeof navigator.share === 'function';

	const editBlocked = designSystemEditingBlocked();

	const close = useCallback(() => {
		onClose();
	}, [onClose]);

	const onAccessChange = useCallback(
		async (newPubliclyReadable: boolean) => {
			try {
				await forge.updateDesignSystem({
					update: {
						id: designSystem.id,
						metadata: {
							...designSystem.metadata,
							publiclyReadable: newPubliclyReadable,
						},
					},
				});
				setPubliclyReadable(newPubliclyReadable);
				trackDesignSystemAction('change_access', { public: newPubliclyReadable });
			} catch (error) {
				toast.error('Failed to update design system access settings');
				console.error('Error updating design system access:', error);
			}
		},
		[designSystem]
	);

	const config: ShareEntityModalConfig = useMemo(
		() => ({
			title: 'Share this design system',
			entityLabel: 'design system',
			editorsOnlyLabel: 'Only design system editors',
			publiclyReadable,
			showPrivateOption: !editBlocked,
			isPubliclyReadableForced: editBlocked === 'read-only',
			canManageAccess: !editBlocked,
			shareUrl: designSystemShareUrl(designSystem),
			shareTitle: designSystem.title ?? 'Design system',
			canNativeShare,
			trackAction: canNativeShare ? 'share_design_system_link' : 'copy_design_system_link',
			onAccessChange,
			onClose: close,
			accessManagerProps: {
				title: 'Manage Design System Editors',
				onClose: close,
				copyLink: {
					shareUrl: designSystemShareUrl(designSystem),
					linkLabel: canNativeShare ? 'Share design system link' : 'Copy design system link',
					canNativeShare,
					shareTitle: designSystem.title ?? 'Design system',
					trackAction: canNativeShare ? 'share_design_system_link' : 'copy_design_system_link',
				},
				userAccess: {
					entityId: designSystem.id,
					getUsers: async (id) => forge.getDesignSystemUsers({ designSystemId: id }),
					grantAccess: async (id, emails) =>
						forge.grantDesignSystemAccess.withToast()({
							designSystemId: id,
							emails,
						}),
					revokeAccess: async (id, email) =>
						forge.revokeDesignSystemAccess.withToast()({
							designSystemId: id,
							email,
						}),
					errorMessages: {
						fetchFailed: 'Failed to fetch design system users',
						revokeFailed: 'Failed to revoke user access',
					},
					onAccessGranted: () => {
						if (user?.id) {
							forge.updateUser({
								update: {
									id: user.id,
									metadata: {
										lastSharedDate: new Date().toISOString(),
									},
								},
							});
						}
					},
				},
				organizationAccess: {
					entityId: designSystem.id,
					getOrganizations: (id) => forge.getDesignSystemOrganizations(id),
					addToOrganization: (entityId, orgId) =>
						forge.addDesignSystemToOrganization.withToast()(entityId, orgId),
					removeFromOrganization: (entityId, orgId) =>
						forge.removeDesignSystemFromOrganization.withToast()(entityId, orgId),
					emptyStateMessage: 'This design system is not shared with any teams',
					fetchFailedMessage: 'Failed to load design system teams',
				},
			},
		}),
		[publiclyReadable, designSystem, canNativeShare, editBlocked, onAccessChange, close, user?.id]
	);

	return <ShareEntityModal config={config} />;
};

export default ShareDesignSystemModal;
